import React from 'react'

import { Formik } from 'formik'
import Yup from '_zari/validation/_yup'

import { ZariContext } from '_zari/context'

import { Button, Form, Modal, Divider, Row, Col } from '_zari/core'

import { MonedasConstant, CondicionesPagoConstant } from 'constants/index'

import * as FormGroups from './formGroups'

const DetraccionValidationSchema = Yup.object().shape({
  tipo: Yup.string().when('usoDetraccion', {
    is: 'true',
    then: Yup.string().required()
  }),
  importe: Yup.number().when('usoDetraccion', {
    is: 'true',
    then: Yup.number().required()
  }),
  fechaDetraccion: Yup.string().when('usoDetraccion', {
    is: 'true',
    then: Yup.string().required()
  }),
  numeroDocumento: Yup.string().when('usoDetraccion', {
    is: 'true',
    then: Yup.string().required()
  }),
  monedaDetraccion: Yup.string().when('usoDetraccion', {
    is: 'true',
    then: Yup.string().required()
  })
})

const ValidationSchema = Yup.object().shape({
  condicionPago: Yup.string().required(),
  fechaPago: Yup.string().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.string().required()
  }),
  monedaPago: Yup.string().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.string().required()
  }),
  cuentaContablePago: Yup.string().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.string().required()
  }),
  importePago: Yup.number().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.number().required()
  }),
  medioDePago: Yup.string().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.string().required()
  }),
  tipoCambio: Yup.number().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.number().required().min(1)
  }),
  flujoEfectivo: Yup.string().when('condicionPago', {
    is: CondicionesPagoConstant.CONTADO,
    then: Yup.string().required()
  })
})

const FormularioPagos = ({
  show,
  handleClose,
  mainFormData,
  submitFunction,
  usaPartida,
  tieneModificacion,
  condicionPagoDefecto
}) => {
  const __zariContext = React.useContext(ZariContext)

  const formikDetraccionRef = React.useRef(null)
  const formikPagoRef = React.useRef(null)

  const formDetraccionInitialValues = React.useMemo(() => {
    return {
      tipo: '',
      porcentaje: '',
      usoDetraccion: 'false',
      importe: '',
      fechaDetraccion: '',
      numeroDocumento: '',
      monedaDetraccion: MonedasConstant.SOLES
    }
  }, [])

  const formInitialValues = React.useMemo(() => {
    const condicionPago = usaPartida
      ? CondicionesPagoConstant.CONTADO
      : condicionPagoDefecto || CondicionesPagoConstant.CREDITO

    return {
      condicionPago: condicionPago,
      fechaPago: mainFormData.fechaDocumento,
      monedaPago: mainFormData.moneda,
      cuentaContablePago: mainFormData.cuentaPago || '1011',
      importeBase: mainFormData.importeTotal,
      importePago: mainFormData.importeTotal,
      medioDePago: '008',
      numeroDocumentoPago: '',
      tipoCambio: 1,
      flujoEfectivo: 'O050'
    }
  }, [mainFormData, usaPartida, condicionPagoDefecto])

  const submitHandler = (data, { setSubmitting }) => {
    mainFormData.pago = data

    if (formikDetraccionRef.current) {
      if (formikDetraccionRef.current.values.usoDetraccion === 'true') {
        if (data.condicionPago === CondicionesPagoConstant.CONTADO) {
          __zariContext.snack('error', 'Cuando se usa detracción el pago siempre es al crédito.')
          setSubmitting(false)

          return
        }

        mainFormData.detraccion = {
          ...formikDetraccionRef.current.values,
          usoDetraccion: formikDetraccionRef.current.values.usoDetraccion === 'true',
          flagAutoDetraccion: formikDetraccionRef.current.values.flagAutoDetraccion === 'true'
        }
      }

      formikDetraccionRef.current
        .validateForm()
        .then(errorsObj => {
          if (Object.keys(errorsObj).length === 0) {
            submitFunction(mainFormData, { setSubmitting })
          } else {
            // TODO
            // Mostrar alerta
            __zariContext.snack('error', 'Verificar campos de detracción.')
            setSubmitting(false)
          }
        })
        .catch(err => {
          setSubmitting(false)
        })
    } else {
      submitFunction(mainFormData, { setSubmitting })
    }
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Row>
        {!tieneModificacion && (
          <Col md={6}>
            <Modal.Header>
              <Modal.Title>DETRACCION</Modal.Title>
            </Modal.Header>
            <Formik
              initialValues={formDetraccionInitialValues}
              validationSchema={DetraccionValidationSchema}
              innerRef={formikDetraccionRef}
            >
              {({ errors, touched, values, isSubmitting, handleChange, setFieldValue, resetForm }) => (
                <>
                  <Modal.Body>
                    <Form>
                      <FormGroups.UsoDetraccionFormGroup
                        iValue={values.usoDetraccion}
                        handleChange={handleChange}
                        resetForm={resetForm}
                        initialValues={formDetraccionInitialValues}
                      />
                      {values.usoDetraccion === 'true' && (
                        <>
                          <FormGroups.TipoDetraccionFormGroup
                            iError={errors.tipo}
                            iTouched={touched.tipo}
                            fIsSubmitting={isSubmitting}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                          />
                          <FormGroups.PorcentajeDetraccionFormGroup iValue={values.porcentaje} />
                          <FormGroups.ImporteDetraccionFormGroup
                            iError={errors.importe}
                            iTouched={touched.importe}
                            fIsSubmitting={isSubmitting}
                          />
                          <FormGroups.NumeroDocumentoFormGroup
                            iError={errors.numeroDocumento}
                            iTouched={touched.numeroDocumento}
                            fIsSubmitting={isSubmitting}
                          />
                          <FormGroups.FechaDetraccionFormGroup
                            iError={errors.fechaDetraccion}
                            iTouched={touched.fechaDetraccion}
                            fIsSubmitting={isSubmitting}
                          />
                          <FormGroups.MonedaDetraccionFormGroup
                            fIsSubmitting={isSubmitting}
                            iError={errors.monedaDetraccion}
                            iTouched={touched.monedaDetraccion}
                          />
                        </>
                      )}
                    </Form>
                  </Modal.Body>
                </>
              )}
            </Formik>
          </Col>
        )}
        <Col md={tieneModificacion ? 12 : 6}>
          <Modal.Header>
            <Modal.Title>PAGO</Modal.Title>
          </Modal.Header>
          <Formik
            initialValues={formInitialValues}
            onSubmit={submitHandler}
            validationSchema={ValidationSchema}
            innerRef={formikPagoRef}
          >
            {({ errors, handleSubmit, isSubmitting, touched, values, setFieldValue, handleChange }) => (
              <Form>
                <Modal.Body>
                  <FormGroups.CondicionPagoFormGroup
                    iValue={values.condicionPago}
                    fIsSubmitting={isSubmitting || usaPartida}
                    iError={errors.condicionPago}
                    iTouched={touched.condicionPago}
                  />
                  {values.condicionPago !== CondicionesPagoConstant.CREDITO && (
                    <>
                      <Divider />
                      <FormGroups.FechaPagoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaPago}
                        iTouched={touched.fechaPago}
                      />
                      <FormGroups.MonedaFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.monedaPago}
                        iTouched={touched.monedaPago}
                      />
                      <FormGroups.CuentaContablePagoFormGroup
                        handleChange={handleChange}
                        iMonedaValue={values.monedaPago}
                        fIsSubmitting={isSubmitting}
                        iError={errors.cuentaContablePago}
                        iTouched={touched.cuentaContablePago}
                        setFieldValue={setFieldValue}
                      />
                      <FormGroups.ImportePagoFormGroup
                        vMoneda={mainFormData.moneda}
                        vImporteBase={values.importeBase}
                        vMonedaPago={values.monedaPago}
                        vTipoCambio={values.tipoCambio}
                        iValue={values.importePago}
                        setFieldValue={setFieldValue}
                      />
                      <FormGroups.MedioDePagoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.medioDePago}
                        iTouched={touched.medioDePago}
                      />
                      <FormGroups.NumeroDocumentoPagoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.numeroDocumentoPago}
                        iTouched={touched.numeroDocumentoPago}
                      />
                      <FormGroups.TipoCambioFormGroup
                        iFechaPagoValue={values.fechaPago}
                        vMoneda={mainFormData.moneda}
                        iMonedaPagoValue={values.monedaPago}
                        setFieldValue={setFieldValue}
                        fIsSubmitting={isSubmitting}
                        iError={errors.tipoCambio}
                        iTouched={touched.tipoCambio}
                      />
                      <FormGroups.FlujoEfectivoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.flujoEfectivo}
                        iTouched={touched.flujoEfectivo}
                      />
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button color='secondary' onClick={handleClose} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <Button id='button-formulariopagos-procesar' disabled={isSubmitting} onClick={handleSubmit}>
                    Procesar
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </Modal>
  )
}
export default React.memo(FormularioPagos)
